import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Save } from 'lucide-react';
import {
  BrandSettingsApi,
  type BrandSettingsItem,
  type Brand,
} from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useBrands, brandLabel } from '@/lib/useBrands';


/** ตารางบอกว่า field ไหนใช้ตอนไหนในการตอบ */
const FIELD_GUIDE: Array<{
  key: keyof BrandSettingsItem;
  label: string;
  placeholder: string;
  usage: string;
  multiline?: boolean;
}> = [
  {
    key: 'displayName',
    label: 'ชื่อแบรนด์',
    placeholder: 'Cher Scrub',
    usage: 'ใช้แทน {{brandName}} (ไม่ได้ใช้ใน reply ปัจจุบัน แต่ keep ไว้)',
  },
  {
    key: 'pageEmoji',
    label: 'Emoji',
    placeholder: '🩺',
    usage: 'แสดงในข้อความ greeting (ไม่ได้ใช้ตรงๆ — keep ไว้)',
  },
  {
    key: 'greetingNew',
    label: 'ทักทายลูกค้าใหม่',
    placeholder: 'สวัสดีค่ะ ยินดีต้อนรับสู่ Cher Scrub 🩺',
    usage: '{{greeting}} สำหรับลูกค้าใหม่ (totalOrders = 0)',
    multiline: true,
  },
  {
    key: 'greetingReturning',
    label: 'ทักทายลูกค้าเก่า',
    placeholder: 'ยินดีต้อนรับกลับค่ะ ขอบคุณที่กลับมา 💕',
    usage: '{{greeting}} สำหรับลูกค้าเก่า (totalOrders > 0)',
    multiline: true,
  },
  {
    key: 'productLine',
    label: 'คำโปรย product line',
    placeholder: 'รุ่น Standard Unisex ใส่ได้ทุกงาน ✨',
    usage: '{{productLine}} — แสดงใต้ greeting (ลูกค้าใหม่)',
  },
  {
    key: 'materialDetails',
    label: 'รายละเอียดเนื้อผ้า / วัสดุ',
    placeholder: '🧵 เนื้อผ้า Freeflex ยืดหยุ่น 4 ทิศทาง',
    usage: '{{materialDetails}} — แสดงท้าย reply',
    multiline: true,
  },
  {
    key: 'leadTimeText',
    label: 'ระยะเวลาส่ง / พรีออเดอร์',
    placeholder: '⏰ พรีออเดอร์ 10-14 วันค่ะ (+3 วันถ้าปักชื่อ)',
    usage: '{{leadTimeText}} — แสดงท้าย reply',
    multiline: true,
  },
  {
    key: 'freeBenefitsText',
    label: 'ของฟรี / สิทธิประโยชน์',
    placeholder: '🎁 ฟรี! ปักชื่อ + จัดส่งฟรี ทุกออเดอร์',
    usage: '{{freeBenefitsText}} — แสดงเหนือสี',
  },
  {
    key: 'colorsText',
    label: 'รายการสีที่มี',
    placeholder: '🎨 สีที่มี\n   เขียว / ดำ / ฟ้า / กรม / ม่วง',
    usage: '{{colorsText}} — multi-line ได้',
    multiline: true,
  },
  {
    key: 'lineUrl',
    label: 'LINE OA URL',
    placeholder: 'https://lin.ee/Za8IaLe',
    usage: '{{lineUrl}} — ใช้ในข้อความติดตามแบบ/สีใหม่',
  },
  {
    key: 'ctaNew',
    label: 'CTA ปิดท้าย (ลูกค้าใหม่)',
    placeholder: 'สนใจชุดสีไหน ไซส์อะไรดีคะ 😊',
    usage: '{{cta}} สำหรับลูกค้าใหม่',
  },
  {
    key: 'ctaReturning',
    label: 'CTA ปิดท้าย (ลูกค้าเก่า)',
    placeholder: 'รอบนี้สนใจสีไหน กี่ชุดดีคะ 😊',
    usage: '{{cta}} สำหรับลูกค้าเก่า',
  },
  // ---- ข้อมูลบัญชีรับโอน (สำหรับ {{bankAccount}} placeholder) ----
  {
    key: 'bankName' as keyof BrandSettingsItem,
    label: '🏦 ธนาคาร (บัญชีรับโอน)',
    placeholder: 'ธนาคารกสิกรไทย',
    usage: 'แสดงเมื่อปิดการขาย — รวมใน {{bankAccount}}',
  },
  {
    key: 'bankAccountNumber' as keyof BrandSettingsItem,
    label: '💳 เลขที่บัญชี',
    placeholder: '123-4-56789-0',
    usage: 'แสดงให้ลูกค้าโอน — รวมใน {{bankAccount}}',
  },
  {
    key: 'bankAccountName' as keyof BrandSettingsItem,
    label: '👤 ชื่อบัญชี',
    placeholder: 'ณ ยา',
    usage: 'ชื่อเจ้าของบัญชี — รวมใน {{bankAccount}} + ใช้ verify slip',
  },
  {
    key: 'bankPromptpay' as keyof BrandSettingsItem,
    label: '📱 พร้อมเพย์ (optional)',
    placeholder: '081-234-5678 หรือเลขบัตรประชาชน',
    usage: 'แสดงเป็นทางเลือก — รวมใน {{bankAccount}}',
  },
  {
    key: 'notes',
    label: 'หมายเหตุ (ใช้ภายใน)',
    placeholder: 'อัพเดทล่าสุด ...',
    usage: 'ไม่ได้ใช้ใน reply — สำหรับ admin จด memo',
  },
];

function HelpBanner() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 text-sm">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 font-medium text-blue-800 hover:underline w-full text-left"
      >
        <Info size={16} />
        Brand Settings ใช้ทำอะไร? — {open ? 'ซ่อน' : 'ดู'}
      </button>
      {open && (
        <div className="mt-3 text-xs space-y-2 text-muted-foreground">
          <p>
            ที่นี่คือ <strong>ข้อความคงที่ของแบรนด์</strong> ที่บอตใช้ประกอบในการตอบลูกค้า.
            แต่ละ field จะถูกแทนที่ใน Reply Template ผ่าน <code>{'{{ชื่อ field}}'}</code>.
          </p>
          <p>
            แก้แล้ว <strong>cache จะถูกล้างอัตโนมัติ</strong> ภายในไม่กี่วินาที — ลองส่งข้อความหาบอตเพื่อตรวจสอบได้เลย.
          </p>
          <p>
            ถ้าจะแก้ <strong>โครงสร้างของ reply</strong> (ลำดับ/รูปแบบ) → ดูแท็บ "Reply Templates" แทน.
          </p>
        </div>
      )}
    </div>
  );
}

export default function BrandSettingsPage() {
  const [brand, setBrand] = useState<Brand>('scrub');
  const _bk = useBrands().keys;
  const BRANDS = _bk.length ? _bk : ['scrub', 'bow'];
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['brand-settings', brand],
    queryFn: () => BrandSettingsApi.list(brand),
  });

  const item = q.data?.[0] ?? null;
  const [draft, setDraft] = useState<Partial<BrandSettingsItem>>({});
  useEffect(() => {
    if (item) setDraft(item);
  }, [item]);

  const updateMut = useMutation({
    mutationFn: (patch: Partial<BrandSettingsItem>) =>
      item ? BrandSettingsApi.update(item.id, patch) : Promise.reject(new Error('no item')),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['brand-settings', brand] });
      toast.success('บันทึกแล้ว — cache ล้างอัตโนมัติ');
    },
    onError: (e) => toast.error(`บันทึกไม่สำเร็จ: ${e.message}`),
  });

  const createMut = useMutation({
    mutationFn: (data: Partial<BrandSettingsItem>) =>
      BrandSettingsApi.create({ ...data, brand }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['brand-settings', brand] });
      toast.success('สร้าง brand settings แล้ว');
    },
    onError: (e) => toast.error(`สร้างไม่สำเร็จ: ${e.message}`),
  });

  const setField = <K extends keyof BrandSettingsItem>(
    key: K,
    value: BrandSettingsItem[K] | string,
  ) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div className="space-y-4">
      <HelpBanner />

      <div className="flex items-center gap-2">
        {BRANDS.map((b) => (
          <Button
            key={b}
            variant={brand === b ? 'default' : 'outline'}
            size="sm"
            onClick={() => setBrand(b)}
          >
            {brandLabel(b)}
          </Button>
        ))}
      </div>

      {q.isLoading && <div className="text-muted-foreground">กำลังโหลด…</div>}

      {!q.isLoading && !item && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 text-sm">
          <p className="mb-2">
            ยังไม่มี Brand Settings สำหรับ <strong>{brand}</strong>
          </p>
          <Button
            size="sm"
            className="btn-cta-primary"
            onClick={() =>
              createMut.mutate({ displayName: brandLabel(brand) })
            }
          >
            สร้าง Brand Settings เริ่มต้น
          </Button>
        </div>
      )}

      {item && (
        <div className="space-y-3">
          {FIELD_GUIDE.map((f) => (
            <div
              key={f.key as string}
              className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start border-b border-border pb-3"
            >
              <div>
                <label className="text-sm font-semibold">{f.label}</label>
                <p className="text-xs text-muted-foreground mt-1">{f.usage}</p>
              </div>
              <div className="lg:col-span-2">
                {f.multiline ? (
                  <textarea
                    className="w-full text-sm border border-input rounded-md p-2 min-h-[80px] font-mono"
                    value={(draft[f.key] as string | null) ?? ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setField(f.key, e.target.value)}
                  />
                ) : (
                  <Input
                    value={(draft[f.key] as string | null) ?? ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setField(f.key, e.target.value)}
                  />
                )}
              </div>
            </div>
          ))}

          <div className="flex items-center gap-2 sticky bottom-0 bg-background py-3 border-t border-border">
            <p className="text-xs text-muted-foreground flex-1">
              {updateMut.isPending ? 'กำลังบันทึก…' : 'แก้แล้วกดบันทึก → cache ล้างอัตโนมัติ'}
            </p>
            <Button
              size="sm"
              className="btn-cta-primary"
              onClick={() => updateMut.mutate(draft)}
              disabled={updateMut.isPending}
            >
              <Save size={14} className="mr-1" /> บันทึก
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
